import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getUnreadNotificationCount,
  subscribeToNotifications,
} from "../lib/supabase";

interface NotificationContextType {
  unreadCount: number;
  isLoading: boolean;
  updateUnreadCount: (count: number) => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const result = await getUnreadNotificationCount(user.id);
      if (result.success && result.count !== undefined) {
        setUnreadCount(result.count);
      }
    } catch (error) {
      console.error("Error loading unread notification count:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const refreshNotifications = useCallback(async () => {
    await loadUnreadCount();
  }, [loadUnreadCount]);

  const updateUnreadCount = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Load initial unread count
  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
    }
  }, [user?.id, loadUnreadCount]);

  // Set up real-time subscription for notification updates
  useEffect(() => {
    if (!user?.id) return;

    const subscription = subscribeToNotifications(user.id, () => {
      // Add a small delay before fetching the unread count
      // This ensures the database transaction is fully committed
      setTimeout(() => {
        loadUnreadCount();
      }, 500);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, loadUnreadCount]);

  const value: NotificationContextType = {
    unreadCount,
    isLoading,
    updateUnreadCount,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
